import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface WisdomEntry {
  id: string;
  workshopId: string | null;
  vehicleCatalogId: string | null;
  make: string | null;
  model: string | null;
  title: string;
  content: string;
  scope: string;
  tags: string[];
  votes: number;
  voted: boolean;
  authorName: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface WisdomFilters {
  make?: string;
  model?: string;
  scope?: 'all' | 'private' | 'public';
  sort?: 'relevance' | 'votes' | 'recent';
}

export function useWisdom(filters: WisdomFilters = {}) {
  const queryClient = useQueryClient();

  const params = new URLSearchParams();
  if (filters.make) params.set('make', filters.make);
  if (filters.model) params.set('model', filters.model);
  if (filters.scope) params.set('scope', filters.scope);
  if (filters.sort) params.set('sort', filters.sort);
  const qs = params.toString();

  const query = useQuery<{ entries: WisdomEntry[] }>({
    queryKey: ['wisdom', filters],
    queryFn: async () => {
      const res = await api.get(`/wisdom${qs ? `?${qs}` : ''}`);
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: {
      title: string;
      content: string;
      make?: string | null;
      model?: string | null;
      scope?: 'private' | 'public';
      tags?: string[];
    }) => {
      const res = await api.post('/wisdom', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wisdom'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      title?: string;
      content?: string;
      scope?: 'private' | 'public';
      tags?: string[];
    }) => {
      const res = await api.patch(`/wisdom/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wisdom'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/wisdom/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wisdom'] });
    },
  });

  const voteMutation = useMutation({
    mutationFn: async ({ id, voted }: { id: string; voted: boolean }) => {
      if (voted) {
        const res = await api.delete(`/wisdom/${id}/vote`);
        return res.data;
      } else {
        const res = await api.post(`/wisdom/${id}/vote`);
        return res.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wisdom'] });
    },
  });

  return {
    entries: query.data?.entries ?? [],
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    refetch: query.refetch,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    remove: deleteMutation.mutateAsync,
    vote: (id: string, voted: boolean) => voteMutation.mutateAsync({ id, voted }),
    isCreating: createMutation.isPending,
  };
}
